/**
 * Authentication Store
 * Global state management for user authentication
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User, Session, AuthState } from '../types/auth.types';

interface AuthStore extends AuthState {
  initialize: () => Promise<void>;
  setUser: (user: User | null, session: Session | null) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/**
 * Auth store using Zustand
 */
export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  session: null,
  loading: true,
  error: null,

  /**
   * Initialize auth state and set up listener
   */
  initialize: async () => {
    try {
      set({ loading: true, error: null });

      // Get initial session
      const { data: { session } } = await supabase.auth.getSession();

      set({
        user: session?.user ?? null,
        session: session ?? null,
        loading: false,
      });

      // Listen for auth state changes
      supabase.auth.onAuthStateChange((_event, session) => {
        set({
          user: session?.user ?? null,
          session: session ?? null,
          loading: false,
        });
      });
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      set({
        error: 'Failed to initialize authentication',
        loading: false,
      });
    }
  },

  /**
   * Set user and session
   */
  setUser: (user, session) => {
    set({ user, session, error: null });
  },

  /**
   * Clear user and session
   */
  clearUser: () => {
    set({ user: null, session: null, error: null });
  },

  /**
   * Set loading state
   */
  setLoading: (loading) => {
    set({ loading });
  },

  /**
   * Set error message
   */
  setError: (error) => {
    set({ error });
  },
}));
